/**
 * SubscribePage.tsx — 邮件订阅
 * 订阅后写入 D1，并由 Resend 发送欢迎邮件（未配置 key 时后端自动降级）。
 */
import { useState, type FormEvent } from 'react';
import { subscribe } from '../lib/api';
import { trackEvent } from '../lib/analytics';

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export default function SubscribePage() {
  const [email, setEmail] = useState('');
  const [status, setStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');
  const [message, setMessage] = useState('');

  async function handleSubmit(event: FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();
    const value = email.trim();
    if (!EMAIL_RE.test(value)) {
      setStatus('error');
      setMessage('请输入正确的邮箱地址');
      return;
    }
    setStatus('submitting');
    try {
      const result = await subscribe(value);
      setStatus('success');
      setMessage(
        result.alreadySubscribed
          ? '该邮箱已订阅过，无需重复订阅。'
          : '订阅成功！欢迎邮件已发送（如未配置 Resend，后端会自动跳过发送）。',
      );
      trackEvent('subscribe', { email: value });
      setEmail('');
    } catch (err) {
      setStatus('error');
      setMessage(err instanceof Error ? err.message : '订阅失败，请稍后重试');
    }
  }

  return (
    <div className="mx-auto max-w-xl space-y-6">
      <div className="text-center">
        <h1 className="text-2xl font-bold text-slate-900">邮件订阅</h1>
        <p className="mt-2 text-sm leading-relaxed text-slate-500">
          订阅后，每当有新的优质 Prompt 上线，我们会第一时间邮件通知你。
          你的邮箱只用于通知，不会收到垃圾邮件。
        </p>
      </div>

      {status === 'success' && (
        <div className="rounded-xl border border-green-200 bg-green-50 p-4 text-sm text-green-700">
          {message}
        </div>
      )}
      {status === 'error' && (
        <div className="rounded-xl border border-red-200 bg-red-50 p-4 text-sm text-red-600">
          {message}
        </div>
      )}

      <form
        onSubmit={handleSubmit}
        className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm"
      >
        <label htmlFor="email" className="mb-1.5 block text-sm font-medium text-slate-700">
          邮箱地址
        </label>
        <input
          id="email"
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="you@example.com"
          className="w-full rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-sm text-slate-800 outline-none transition focus:border-indigo-400 focus:ring-2 focus:ring-indigo-100"
        />
        <button
          type="submit"
          disabled={status === 'submitting'}
          className="mt-4 w-full rounded-xl bg-indigo-600 py-3 text-sm font-semibold text-white shadow-sm transition hover:bg-indigo-700 disabled:cursor-not-allowed disabled:opacity-60"
        >
          {status === 'submitting' ? '提交中…' : '订阅新 Prompt 通知'}
        </button>
      </form>
    </div>
  );
}
